// ════════════════════════════════════════════════════════════════════
// src/services/kanbanMesclar.js — juntar o Kanban de várias pessoas
// ════════════════════════════════════════════════════════════════════
// O Kanban da conta é UM documento (users/{conta}/kanban/data) que a
// equipe inteira edita. Antes, cada pessoa gravava a cópia inteira que
// tinha aberto e apagava o que os outros tinham posto nesse meio tempo.
//
// Agora a gravação junta três versões:
//   base  → a última versão do servidor que esta tela conhecia
//   local → o que esta pessoa tem na tela
//   serv  → o que está no servidor agora (com o que os outros fizeram)
// O que esta pessoa mudou (local ≠ base) vai por cima do servidor; o
// resto fica como os outros deixaram. Card por card, campo por campo.
// ════════════════════════════════════════════════════════════════════

export const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const porId = (lista) => new Map((lista || []).map((x) => [x.id, x]));

// Campos simples: o que mudou aqui vence; o resto vem do servidor.
// `listas`: campos que são lista e têm o próprio jeito de juntar.
function mesclarCampos(base, local, serv, listas = {}) {
  const r = { ...serv };
  for (const k of new Set([...Object.keys(local || {}), ...Object.keys(base || {})])) {
    if (listas[k]) continue;
    if (!igual(local?.[k], base?.[k])) {
      if (local && k in local) r[k] = local[k]; else delete r[k];
    }
  }
  for (const [k, juntar] of Object.entries(listas)) {
    const v = juntar(base?.[k], local?.[k], serv?.[k]);
    if (v !== undefined) r[k] = v;
  }
  return r;
}

// Lista de itens com `id` (quadros, colunas, cards). A ordem é a do
// servidor; o que esta pessoa criou entra no fim.
function mesclarLista(base, local, serv, mesclarItem) {
  if (local === undefined && serv === undefined) return undefined;
  const b = porId(base), l = porId(local), s = porId(serv);
  const saida = [];
  for (const item of serv || []) {
    const id = item.id;
    if (!b.has(id)) { saida.push(item); continue; }   // outra pessoa criou
    if (!l.has(id)) continue;                         // esta pessoa apagou
    const bi = b.get(id), li = l.get(id);
    if (igual(li, bi)) saida.push(item);              // só os outros mexeram
    else if (igual(item, bi)) saida.push(li);         // só esta pessoa mexeu
    else saida.push(mesclarItem(bi, li, item));       // os dois mexeram
  }
  for (const item of local || []) {
    if (!b.has(item.id) && !s.has(item.id)) saida.push(item);   // esta pessoa criou
  }
  return saida;
}

// Anotações só se acumulam: as novas daqui entram na frente das do servidor.
function mesclarHistorico(base, local, serv) {
  if (local === undefined && serv === undefined) return undefined;
  const ja = (lista, n) => (lista || []).some((x) => igual(x, n));
  const novas = (local || []).filter((n) => !ja(base, n) && !ja(serv, n));
  return [...novas, ...(serv || [])];
}

const mesclarCard = (b, l, s) => mesclarCampos(b, l, s, { historico: mesclarHistorico });
const mesclarColuna = (b, l, s) => mesclarCampos(b, l, s);

export const mesclarQuadros = (base, local, serv) => mesclarLista(base, local, serv, (b, l, s) =>
  mesclarCampos(b, l, s, {
    colunas: (bb, ll, ss) => mesclarLista(bb, ll, ss, mesclarColuna),
    cards:   (bb, ll, ss) => mesclarLista(bb, ll, ss, mesclarCard),
  }));
